import { useEffect, useState } from 'react';
import { Heart, Shield, Leaf, HandHeart, Globe, Instagram, Facebook } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const Performers = () => {
  const [serviceGroups, setServiceGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/hero-masters`)
      .then((res) => res.json())
      .then((data) => {
        // Map API field full_description -> fullDescription for compatibility
        const mapped = Array.isArray(data)
          ? data.map((g) => ({ ...g, fullDescription: g.full_description }))
          : [];
        setServiceGroups(mapped);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const categoryIcons = {
    'Education & Therapy': Heart,
    'Conservation': Leaf,
    'Law Enforcement': Shield,
    'Assistance': HandHeart
  };

  const categoryColors = {
    'Education & Therapy': 'bg-rose-100 text-rose-700 border-rose-200',
    'Conservation': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'Law Enforcement': 'bg-blue-100 text-blue-700 border-blue-200',
    'Assistance': 'bg-amber-100 text-amber-700 border-amber-200'
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1696271929832-6a91a90b5884?w=1920&q=80"
            alt="Service dog"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-cyan-50/90 to-white"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200">
              Service Groups
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              Meet Our Hero Dog Performers
            </h1>
            <p className="text-lg text-slate-600">
              Discover the incredible organizations and their amazing dogs who serve our community. 
              Watch demonstrations and learn about the life-changing work these heroes do every day.
            </p>
          </div>
        </div>
      </section>

      {/* Service Groups Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <p className="text-center text-slate-500">Loading Hero Masters...</p>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {serviceGroups.map((group) => {
                const Icon = categoryIcons[group.category] || Heart;
                const colorClass = categoryColors[group.category];
                
                return (
                  <Card 
                    key={group.id} 
                    className="border-slate-200 hover:border-cyan-300 hover:shadow-xl transition-all duration-300 overflow-hidden"
                  >
                    {group.image_url && (
                      <div className="relative aspect-[16/9] bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center border-b border-slate-100">
                        <img
                          src={group.image_url}
                          alt={`${group.name} — Hero Master`}
                          className="w-full h-full object-contain p-3"
                          loading="lazy"
                          data-testid={`hero-master-image-${group.id}`}
                        />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-start justify-between mb-3">
                        <div className="bg-gradient-to-br from-cyan-100 to-teal-100 w-12 h-12 rounded-lg flex items-center justify-center">
                          <Icon className="h-6 w-6 text-cyan-700" />
                        </div>
                        <Badge variant="outline" className={colorClass}>
                          {group.category}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl">{group.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <CardDescription className="text-slate-600 text-base leading-relaxed">
                        {group.fullDescription || group.description}
                      </CardDescription>
                      
                      <div className="flex flex-wrap gap-3 pt-2">
                        {group.website && (
                          <Button asChild variant="outline" size="sm" className="border-cyan-200 text-cyan-700 hover:bg-cyan-50">
                            <a href={group.website} target="_blank" rel="noopener noreferrer">
                              <Globe className="h-4 w-4 mr-2" />
                              Website
                            </a>
                          </Button>
                        )}
                        {group.facebook && (
                          <Button asChild variant="outline" size="sm" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                            <a href={group.facebook} target="_blank" rel="noopener noreferrer">
                              <Facebook className="h-4 w-4 mr-2" />
                              Facebook
                            </a>
                          </Button>
                        )}
                        {group.instagram && (
                          <Button asChild variant="outline" size="sm" className="border-pink-200 text-pink-700 hover:bg-pink-50">
                            <a href={group.instagram} target="_blank" rel="noopener noreferrer">
                              <Instagram className="h-4 w-4 mr-2" />
                              Instagram
                            </a>
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            )}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
                What to Expect
              </h2>
              <p className="text-lg text-slate-600">
                Meet the incredible teams behind these hero dogs
              </p>
            </div>

            {/* Hero image for the section */}
            <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl bg-slate-900">
              <img
                src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/dcl6il96_1F67C3A7-79F2-4F35-A462-8FABC31D9414_1_105_c.jpeg"
                alt="A Border Collie soaring mid-air through the agility course"
                className="w-full h-auto max-h-[600px] object-contain"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 rounded-full p-3 flex-shrink-0">
                    <Heart className="h-6 w-6 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-slate-800 mb-2">
                      Meet & Greet
                    </h3>
                    <p className="text-slate-600">
                      Our Master of Ceremonies will move about the park, introduce each of our participants, and interview the fur-contestant and their owner!
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-cyan-100 rounded-full p-3 flex-shrink-0">
                    <Shield className="h-6 w-6 text-cyan-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-slate-800 mb-2">
                      Educational Sessions
                    </h3>
                    <p className="text-slate-600">
                      Talk to our Hero Masters and learn about what goes into training these wonderful dogs, what they can do and how they can help you.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 rounded-full p-3 flex-shrink-0">
                    <Leaf className="h-6 w-6 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-slate-800 mb-2">
                      Support Their Mission
                    </h3>
                    <p className="text-slate-600">
                      Discover how you can support these organizations through volunteering, donations, or other ways to help.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Performers;
